import { CheckCircle2, Clock, ShieldAlert } from 'lucide-react'
import type { ProfileResponse } from '../../types/api'

/**
 * What an institution sees in place of the tutor's verification form.
 *
 * <p>There is nothing to submit: a school, college or company is checked by a visit to its
 * premises against the public registry code it gave at registration, and that request reached the
 * ops queue the moment it registered. This card exists so the absence of a button is explained
 * rather than looking like a missing feature - and so the consequence is stated plainly, because
 * an institution that does not know it is invisible will assume the map is broken.
 */
export function InstitutionVerificationStatus({
  profile,
  note,
}: {
  profile: ProfileResponse
  /** The reviewing admin's note, from trust status - the actionable half of a rejection. */
  note?: string
}) {
  const status = profile.verificationStatus

  if (status === 'VERIFIED') {
    return (
      <div className="card-pad">
        <div className="flex items-start gap-2.5">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
          <div>
            <div className="font-bold text-ink-900">Verified</div>
            <p className="mt-1 text-sm text-ink-500">
              Your dot is live on the map and learners nearby can send you connection requests.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'SUSPENDED') {
    return (
      <div className="card-pad border-red-200 bg-red-50">
        <div className="flex items-start gap-2.5">
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-red-600" />
          <div>
            <div className="font-bold text-ink-900">Suspended</div>
            <p className="mt-1 text-sm text-ink-600">
              Your listing is not visible. Contact the YellowDots team to resolve this.
            </p>
            {note && (
              <p className="mt-2 text-sm text-ink-600">{note}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // UNVERIFIED covers both "waiting" and "turned down"; the note is what tells them apart, since
  // a rejection returns the account to UNVERIFIED with the reviewer's reason attached.
  const wasReviewed = Boolean(note)

  return (
    <div className="card-pad border-sun-400/60 bg-sun-400/10">
      <div className="flex items-start gap-2.5">
        <Clock size={18} className="mt-0.5 shrink-0 text-amber-700" />
        <div>
          <div className="font-bold text-ink-900">
            {wasReviewed ? 'Needs attention' : 'With the verification team'}
          </div>
          <p className="mt-1 text-sm text-ink-600">
            {wasReviewed
              ? 'The team has looked at your registration and needs something changed before it can be approved.'
              : 'Your registration went to the YellowDots team when you signed up. They verify institutions by visiting the premises and checking the registry code you gave - there is nothing for you to upload.'}
          </p>
          {note && (
            <p className="mt-2 rounded-lg bg-white/70 px-3 py-2 text-sm text-ink-700">
              {note}
            </p>
          )}
          <p className="mt-2 text-sm font-semibold text-ink-900">
            Until then your dot is not on the map, and you cannot send or receive connection
            requests.
          </p>
        </div>
      </div>
    </div>
  )
}
